import { inject, Injectable } from '@angular/core';
import { digger } from '@cbre/digger';
import {
  AvailabilityColumns,
  Column,
  CompanyColumns,
  LeaseColumns,
  PropertyColumns,
  SaleColumns,
  SpaceColumns,
  TenancyColumns,
} from '@miq/catalog';
import {
  CE_COMMA_DOT_DECIMAL_LOCALES,
  CE_COMMA_SPACE_DECIMAL_LOCALES,
  ClipboardAndExportColumnFields,
  DataObject,
  EntityType,
  sortByDisplayOrder,
} from '@miq/shared-models';
import { CATALOG_COLUMN_EXTENSIONS, LookupRefService, UserInfoResolver, UserLocaleService } from '@miq/platform';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import moment from 'moment';
import { catchError, distinct, filter, forkJoin, from, map, mergeMap, Observable, of, reduce } from 'rxjs';
import * as XLSX from 'xlsx';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { getCurrentLocale } from '@miq/utils';
import { DataCellFormatterPipe } from '../pipes/data-cell-formatter.pipe';
import {
  buildingFacingDirectionFormatter,
  propertyElementTypeFormatter,
  propertyMeasurementElementNumberFormatter,
  propertyMeasurementValueFormatter,
} from '../catalog/measurement-formatters';
import { applyContactDetailsColumnWidths, getContactDetailsColumnIndexes } from './excel-export.helpers';
import {
  CONTACT_CELL_CHILD_INDENT,
  CONTACT_CELL_COMPANY_BULLET,
  CONTACT_CELL_CONTACT_BULLET,
  europeanLocales,
  lookupDynamicExtensions,
} from './excel.constants';

/**
 * Excel / CSV generation for the clipboard, the record list and report data views.
 *
 * L2-S4: moved verbatim out of `src/app/shared/services/excel-shared.service.ts` (now a shim). It
 * landed in @miq/platform-ui rather than @miq/platform because it calls
 * `DataCellFormatterPipe.extractValue`, and @miq/platform is built BEFORE @miq/platform-ui — the
 * dependency only runs one way. Every injected dependency (LookupRefService, UserInfoResolver,
 * UserLocaleService, CbreLoggerToken, CATALOG_COLUMN_EXTENSIONS) already lived in a library; what
 * moved with it were four leaf modules (excel-export.helpers, excel.constants, the measurement
 * formatters, and `sortByDisplayOrder`), each of which the old host path now re-exports.
 *
 * Stateless: every field is a readonly injected dependency and all output is derived from the
 * arguments, so a second copy in a remote bundle would be harmless — but it is a federation-shared
 * lib, so there is only one.
 */
export enum FeatureInUseExcel {
  Clipboard = 'Clipboard',
  Report = 'DataView',
}
const FLOOR_SUITE_FIELDS_LIST = ['floor_suite.suite_number', 'floor_suite/suite_number', 'property_space/suite_number'];
const COLUMNS_TO_TRANSLATE = [
  PropertyColumns.Location_Market,
  PropertyColumns.Location_Submarket,
  CompanyColumns.Locations_Market,
  CompanyColumns.Locations_Submarket,
  LeaseColumns.Property_Market,
  LeaseColumns.Property_Submarket,
  SaleColumns.Property_Market,
  SaleColumns.Property_Submarket,
  AvailabilityColumns.Property_Market,
  AvailabilityColumns.Property_Submarket,
  TenancyColumns.Property_Market,
  TenancyColumns.Property_Submarket,
];
const COLUMNS_TO_FORMAT = [
  PropertyColumns.Location_Submarket,
  CompanyColumns.Locations_Submarket,
  LeaseColumns.Property_Submarket,
  SaleColumns.Property_Submarket,
  AvailabilityColumns.Property_Submarket,
  TenancyColumns.Property_Submarket,
];
interface CompanyContactRoleAddress {
  readonly role_desc?: string;
  readonly company_name?: string;
  readonly contact_id?: number | null;
  readonly contact_display_name?: string;
  readonly first_name?: string;
  readonly last_name?: string;
  readonly phones?: ReadonlyArray<{ readonly full_phone_number?: string }>;
  readonly primary_f?: boolean;
}

interface ContactDetailsGroup {
  readonly company: string;
  readonly contacts: Array<{ readonly name: string; readonly phone: string }>;
}
@Injectable({
  providedIn: 'root',
})
export class ExcelSharedService {
  /**
   * L2-S1/T4: was `import { EXTENDED_COLUMNS } from '@records/catalog/columns-override.config'`.
   * That registry value-imports ~128 records/controls modules (component classes included), so
   * this shared service — reachable from the reports remote — pulled the whole records graph
   * into every bundle. Only `appendUOMColumns` ever reads it, and only for `.uomId`.
   * Optional + `?? {}` so a TestBed that does not register the token behaves exactly like a
   * shell whose registry has no entry for the column (the pre-existing `?.` guard already
   * covers that case).
   */
  private readonly columnExtensions = inject(CATALOG_COLUMN_EXTENSIONS, { optional: true }) ?? {};
  private readonly lookupRefService = inject(LookupRefService);
  private readonly userInfoResolver = inject(UserInfoResolver);
  private readonly logger = inject<CbreLogger>(CbreLoggerToken);
  private readonly userLocale = inject(UserLocaleService);
  private getDyanmicLovs(
    record: DataObject,
    column: Column,
    lookupDyanmicExtension: string,
    filterDuplicates: boolean
  ): Observable<string> {
    const user = this.userInfoResolver.resolve();
    const lookupData = digger(record.data, column.field);
    const refValues = digger(record.data, lookupDyanmicExtension);
    let dynamicLovs$ = from(refValues).pipe(
      mergeMap((value, index) => {
        const lookupUrl = this.userLocale.isChinaORHongKongUser(user)
          ? `${column.lookupCategory}`
          : `${column.lookupCategory}?_payload=${value}`;
        return this.lookupRefService.loadDataFor(lookupUrl, false, false, [value]).pipe(
          map(lovList => {
            const lov = lovList.find(lovItem => lovItem.key === lookupData[index]);
            return lov?.value || '';
          })
        );
      })
    );
    if (filterDuplicates) {
      dynamicLovs$ = dynamicLovs$.pipe(
        distinct(),
        filter(lov => !!lov)
      );
    }
    return dynamicLovs$.pipe(
      reduce((acc, lov) => {
        acc += `${lov},`;
        return acc;
      }, ''),
      map(res => res.slice(0, -1))
    );
  }
  private getAndFormatValueAsObservable(
    record: DataObject,
    column: Column,
    filterDuplicates: boolean
  ): Observable<string[]> {
    let lovFormatter;
    if (column.columnId === SpaceColumns.SpaceDetails_PrimaryContactDetails) {
      return of([
        this.buildContactDetails(
          record,
          contact => contact.primary_f === true && contact.contact_id != null,
          'Company'
        ),
      ]);
    }
    if (column.columnId === SpaceColumns.SpaceDetails_LandlordRepresentativeDetails) {
      return of([
        this.buildContactDetails(
          record,
          contact => contact.role_desc?.toLowerCase() === 'lessor',
          'Landlord company'
        ),
      ]);
    }
    // Special case for availability measurement type - add this check
    if (column.columnId === AvailabilityColumns.AvailabilityMeasurements_Type) {
      const formattedValue = propertyElementTypeFormatter(record?.data, column);
      return of([formattedValue]);
    }
    if (column.columnId === PropertyColumns.PropertyMeasurements_Type) {
      const formattedValue = propertyElementTypeFormatter(record?.data, column);
      return of([formattedValue]);
    }
    if (column.columnId === PropertyColumns.PropertyMeasurements_Value) {
      const formattedValue = propertyMeasurementValueFormatter(record?.data, column);
      return of([formattedValue]);
    }
    if (column.columnId === PropertyColumns.PropertyMeasurements_ElementNumber) {
      const formattedValue = propertyMeasurementElementNumberFormatter(record?.data, column);
      return of([formattedValue]);
    }
    if (column.columnId === PropertyColumns.PropertyTypeDetails_Facing) {
      const formattedValue = buildingFacingDirectionFormatter(record?.data, column);
      return of([formattedValue]);
    }
    // Special case for availability measurement value
    if (column.columnId === AvailabilityColumns.AvailabilityMeasurements_Value) {
      const formattedValue = propertyMeasurementValueFormatter(record?.data, column);
      return of([formattedValue]);
    }
    // Special case for availability measurement value
    if (column.columnId === AvailabilityColumns.AvailabilityMeasurements_Type) {
      const formattedValue = propertyElementTypeFormatter(record?.data, column);
      return of([formattedValue]);
    }
    // Special case for availability measurement value
    if (column.columnId === AvailabilityColumns.AvailabilityMeasurements_ElementNumber) {
      const formattedValue = propertyMeasurementElementNumberFormatter(record?.data, column);
      return of([formattedValue]);
    }
    // Special case for availability measurement value
    if (column.columnId === LeaseColumns.LeaseMeasurements_Value) {
      const formattedValue = propertyMeasurementValueFormatter(record?.data, column);
      return of([formattedValue]);
    }
    // Special case for availability measurement Type
    if (column.columnId === LeaseColumns.LeaseMeasurements_Type) {
      const formattedValue = propertyElementTypeFormatter(record?.data, column);
      return of([formattedValue]);
    }
    // Special case for availability measurement Unit
    if (column.columnId === LeaseColumns.LeaseMeasurements_Unit) {
      const formattedValue = propertyMeasurementElementNumberFormatter(record?.data, column);
      return of([formattedValue]);
    }
    if (column.columnId === SpaceColumns.SpaceDetails_InshopBreakDown_Notes) {
      const items: any[] = record.data?.property_measurement_availability;
      if (!Array.isArray(items) || items.length === 0) return of(['']);
      const values = sortByDisplayOrder(items)
        .map((item: any) => item?.property_measurement_notes)
        .filter((v: any) => v != null && v !== '');
      return of([values.length > 0 ? values.join(' | ') : '']);
    }
    if (column.columnId === SpaceColumns.SpaceDetails_InshopBreakDown_Size) {
      const items: any[] = record.data?.property_measurement_availability;
      if (!Array.isArray(items) || items.length === 0) return of(['']);
      const values = sortByDisplayOrder(items)
        .map((item: any) => item?.property_measurement_size)
        .filter((v: any) => v != null && v !== '');
      return of([values.length > 0 ? values.join(' | ') : '']);
    }
    if (COLUMNS_TO_TRANSLATE.includes(column.columnId)) {
      column.lookupValueKey = 'name';
    }
    if (COLUMNS_TO_FORMAT.includes(column.columnId)) {
      lovFormatter = (lovValue: DataObject) => lovValue.value.split(' -')[0];
      filterDuplicates = true;
    }
    let formattedObjData = DataCellFormatterPipe.extractValue(
      record.data,
      { ...column, filterDuplicates: 'filterDuplicates' in column ? column.filterDuplicates : filterDuplicates },
      lovFormatter
    );
    if (FLOOR_SUITE_FIELDS_LIST.includes(column.field)) {
      formattedObjData = ` ${formattedObjData}`;
    }
    return of(formattedObjData);
  }
  private buildContactDetails(
    record: DataObject,
    predicate: (contact: CompanyContactRoleAddress) => boolean,
    defaultCompanyName: string
  ): string {
    const contacts = this.extractContactData(record?.data);
    const groups = contacts.filter(predicate).reduce<Record<string, ContactDetailsGroup>>((acc, contact) => {
      const company = contact.company_name?.trim() || defaultCompanyName;
      if (!acc[company]) acc[company] = { company, contacts: [] };
      const contactInfo = this.extractContactInfo(contact);
      if (contactInfo.name || contactInfo.phone) acc[company].contacts.push(contactInfo);
      return acc;
    }, {});

    return Object.values(groups)
      .flatMap(group => [
        // Match the grid renderer hierarchy while keeping source text ASCII.
        `${CONTACT_CELL_COMPANY_BULLET} ${group.company}`,
        ...group.contacts.flatMap(contact => [
          ...(contact.name ? [`${CONTACT_CELL_CHILD_INDENT}${CONTACT_CELL_CONTACT_BULLET} ${contact.name}`] : []),
          ...(contact.phone ? [`${CONTACT_CELL_CHILD_INDENT}${CONTACT_CELL_CONTACT_BULLET} ${contact.phone}`] : []),
        ]),
      ])
      .join('\n');
  }
  private extractContactData(recordData: DataObject): CompanyContactRoleAddress[] {
    return Array.isArray(recordData?.company_contact_role_addresses)
      ? recordData.company_contact_role_addresses
      : [];
  }
  private extractContactInfo(contact: CompanyContactRoleAddress): { name: string; phone: string } {
    const name = contact.contact_display_name || [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    const phone = contact.phones?.map(phoneInfo => phoneInfo.full_phone_number).filter(Boolean).join(' | ') || '';
    return { name: name || '', phone: phone || '' };
  }
  private getFormatterToUse(
    record: DataObject,
    column: Column,
    entity: EntityType,
    filterDuplicates: boolean
  ): Observable<string[] | string> {
    const lookupDynamicExtension = lookupDynamicExtensions[entity] && lookupDynamicExtensions[entity][column.field];
    return lookupDynamicExtension
      ? this.getDyanmicLovs(record, column, lookupDynamicExtension, filterDuplicates)
      : this.getAndFormatValueAsObservable(record, column, filterDuplicates);
  }
  public getFormattedValuesAsObservables(
    records: DataObject[],
    flatColumns: Column[],
    columnFields: ClipboardAndExportColumnFields[],
    entity: EntityType,
    filterDuplicates = false
  ): Observable<string[][]> {
    try {
      const listOfRecordObservables$ = [of(flatColumns.map(item => item.header))];
      for (const record of records) {
        const listOfValueObservables$: Observable<string[] | string>[] = [];
        for (const flatColumn of flatColumns) {
          if (!!columnFields && columnFields.length > 0) {
            const column = columnFields.find(eachColumn => eachColumn.field === flatColumn.field);
            if (column) {
              flatColumn.values = column.values;
            }
          }
          // Wrap individual formatter calls with error handling
          const formatterObservable$ = this.getFormatterToUse(record, flatColumn, entity, filterDuplicates).pipe(
            catchError(error => {
              return of(''); // Return empty string for failed formatting
            })
          );
          listOfValueObservables$.push(formatterObservable$);
        }
        listOfRecordObservables$.push(forkJoin(listOfValueObservables$) as Observable<string[]>);
      }
      return forkJoin(listOfRecordObservables$).pipe(
        catchError(error => {
          return of([flatColumns.map(item => item.header)]);
        })
      );
    } catch (error) {
      // Handle synchronous errors
      return of([flatColumns.map(item => item.header)]);
    }
  }
  public getFormattedDataToExport(
    records: DataObject[],
    flatColumns: Column[],
    columnFields: ClipboardAndExportColumnFields[],
    filterDuplicates = false
  ): DataObject[] {
    const formattedDataToExport = [];
    formattedDataToExport.push(flatColumns.map(item => item.header));
    for (const record of records) {
      const formattedObj = [];
      for (const item of flatColumns) {
        const tempItem = columnFields.find(eachColumn => eachColumn.field === item.field);
        if (tempItem) {
          item.values = tempItem.values;
        }
        formattedObj.push(
          DataCellFormatterPipe.extractValue(record.data, {
            ...item,
            filterDuplicates,
          })
        );
      }
      formattedDataToExport.push(formattedObj);
    }
    return formattedDataToExport;
  }
  public downloadWorkbookWithExcelJs(workBook: Workbook, fileName: string): void {
    workBook.xlsx.writeBuffer().then(data => {
      const blob = new Blob([data]);
      saveAs(blob, `${fileName}.xlsx`);
    });
  }
  public formatCellValue(value: string): string | DataObject {
    // regex to check numbers, commas and decimal values - to resolve/minimize 'Number stored as Text' warnings in excel.
    const regex = /^(\d{1,3}(,\d{3})*|\d+)(\.\d+\/?)?$/;
    return regex.test(value) ? { formula: `VALUE("${value}")` } : value;
  }
  public buildAndDownloadwithExcelJs(dataToExport: DataObject[], entity: EntityType, fileName: string): void {
    const workBook = new Workbook();
    const workSheet = workBook.addWorksheet(`${entity}`);
    for (const [rowIndex, rowData] of dataToExport.entries()) {
      const rowInstance = workSheet.getRow(rowIndex + 1);
      let columnNumber = dataToExport[0].length;
      while (columnNumber > 0) {
        const cell = rowInstance.getCell(columnNumber);
        columnNumber--;
        cell.value = this.formatCellValue(rowData[columnNumber]);
      }
    }
    this.downloadWorkbookWithExcelJs(workBook, fileName);
  }
  public buildAndDownloadWithSheetJs(
    dataToExport: string[][],
    entity: EntityType,
    fileName: string,
    skipRounding: boolean = false
  ): void {
    if (skipRounding) {
      this.buildAndDownloadAsIs(dataToExport, entity, fileName);
      return;
    }
    const workBook = XLSX.utils.book_new(); // create a new blank book
    // Check if it's a CSV file to apply special formatting
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    const isCSV = fileExtension === 'csv';
    // Pre-process data for CSV to prevent Excel auto-conversion
    let processedData = dataToExport;
    const effectiveLocale = getCurrentLocale();
    const isCESpaceLocale = CE_COMMA_SPACE_DECIMAL_LOCALES.includes(effectiveLocale);
    const isCEDotLocale = CE_COMMA_DOT_DECIMAL_LOCALES.includes(effectiveLocale);

    if (isCSV) {
      // === CSV FILE PROCESSING ===
      // Detect user's locale to determine number format
      const userLocale = navigator.language || navigator.languages?.[0] || 'en-US';

      const [languageCode] = userLocale.toLowerCase().split('-');
      const isEuropeanLocale = europeanLocales.has(languageCode);
      // Pre-process data for CSV to prevent Excel auto-conversion
      processedData = dataToExport.map((row, rowIndex) =>
        row.map(cell => {
          if (typeof cell === 'string') {
            const hyphenatedNumericRegex = /^[0-9]+-[0-9-]+$/;
            const usNumberRegex = /^(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/;
            const europeanNumberRegex = /^(\d{1,3}(\.\d{3})*|\d+)(,\d+)?$/;
            // Wrap hyphenated numeric strings (like ZIP codes) to preserve as text
            if (hyphenatedNumericRegex.test(cell) && !usNumberRegex.test(cell) && !europeanNumberRegex.test(cell)) {
              return `="${cell}"`;
            }
            // Convert number format based on locale for CSV
            // Data from frontend is always in US format, convert for European or Polish users
            if ((isEuropeanLocale || isCESpaceLocale || isCEDotLocale) && usNumberRegex.test(cell)) {
              if (isCESpaceLocale) {
                // Space+comma CE format (pl-PL, fi-FI, hu-HU, etc.): 1 234,56
                return this.formatCESpaceLocale(cell);
              } else if (isCEDotLocale) {
                // Dot+comma CE format (de-DE, de-AT, it-IT, etc.): 1.234,56
                return this.formatNumberForCEDot(cell);
              } else {
                // European format based on browser locale
                const hasDecimal = cell.includes('.');
                if (hasDecimal) {
                  // Has decimal: convert comma to period (thousands) and period to comma (decimal)
                  return cell.replace(/,/g, 'TEMP').replace(/\./g, ',').replace(/TEMP/g, '.');
                } else {
                  // No decimal: just convert comma to period
                  return cell.replace(/,/g, '.');
                }
              }
            }
          }
          return cell;
        })
      );
      const workSheet = XLSX.utils.aoa_to_sheet(processedData);
      XLSX.utils.book_append_sheet(workBook, workSheet, `${entity}`);
      // Generate and save CSV file
      const delimiter = this.getCSVDelimiterForLocale(userLocale);
      let csv = XLSX.utils.sheet_to_csv(workSheet, { FS: delimiter });
      // Normalize line endings: replace all line breaks with CRLF for Windows Excel
      csv = csv.replace(/\r?\n/g, '\r\n');
      // Keep the UTF-8 BOM first for Unicode detection; add sep= only when Excel needs the semicolon delimiter hint.
      const BOM = '\uFEFF';
      const separatorDeclaration = delimiter === ';' ? `sep=${delimiter}\r\n` : '';
      const csvWithBOM = BOM + separatorDeclaration + csv;
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, fileName);
    } else {
      // === EXCEL FILE PROCESSING ===
      const workSheet = XLSX.utils.aoa_to_sheet(processedData);
      const contactDetailsColumnIndexes = getContactDetailsColumnIndexes(processedData[0] ?? []);
      // Convert string numbers to actual numbers for formulas to work
      for (const cellAddress in workSheet) {
        if (!workSheet.hasOwnProperty(cellAddress)) continue;
        const cell = workSheet[cellAddress];
        if (cell && cell.v && typeof cell.v === 'string') {
          // Check if it's a number with US format (comma as thousand separator)
          const usNumberRegex = /^-?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/;
          // Check if it's a hyphenated numeric string (ZIP codes, phone numbers)
          const hyphenatedNumericRegex = /^[0-9]+-[0-9-]+$/;

          if (usNumberRegex.test(cell.v) && !hyphenatedNumericRegex.test(cell.v)) {
            // Convert string number to actual number: "80,000" -> 80000
            const numericValue = parseFloat(cell.v.replace(/,/g, ''));
            // Data is converted to US Format, here if number has comma means then need to convert CE Number format based on user locale
            if (!isNaN(numericValue)) {
              if (isCESpaceLocale) {
                // Space+comma CE format (pl-PL, fi-FI, etc.): keep as text "1 234,56"
                cell.v = this.formatCESpaceLocale(cell.v);
                cell.t = 's'; // Set as string/text
              } else if (isCEDotLocale) {
                // Dot+comma CE format (de-DE, de-AT, etc.): keep as text "1.234,56"
                cell.v = this.formatNumberForCEDot(cell.v);
                cell.t = 's'; // Set as string/text
              } else {
                // For other locales, convert to numeric type
                const hasDecimal = cell.v.includes('.');
                cell.v = numericValue;
                cell.t = 'n'; // Set cell type to number
                cell.z = hasDecimal ? '#,##0.00' : '#,##0';
              }
            }
          } else if (hyphenatedNumericRegex.test(cell.v)) {
            // Keep hyphenated strings as text with space prefix
            cell.v = ` ${cell.v}`;
          }
        }
      }
      applyContactDetailsColumnWidths(workSheet, contactDetailsColumnIndexes);
      XLSX.utils.book_append_sheet(workBook, workSheet, `${entity}`);
      // Save Excel file (.xlsx)
      XLSX.writeFile(workBook, fileName);
    }
  }
  private buildAndDownloadAsIs(dataToExport: string[][], entity: EntityType, fileName: string): void {
    const workBook = XLSX.utils.book_new();
    // Wrap each cell as an explicit string-typed cell object so SheetJS never
    // auto-converts coordinate or numeric values — preserving exact raw values.
    const rawData = dataToExport.map(row => row.map(cell => ({ v: cell ?? '', t: 's' })));
    const workSheet = XLSX.utils.aoa_to_sheet(rawData);
    XLSX.utils.book_append_sheet(workBook, workSheet, `${entity}`);
    XLSX.writeFile(workBook, fileName);
  }
  private formatCESpaceLocale(numberStr: string): string {
    // Convert US format (1234.56 or 1,234.56) to Polish format (1 234,56)
    // First, parse the number
    const numericValue = parseFloat(numberStr.replace(/,/g, ''));

    if (isNaN(numericValue)) {
      return numberStr;
    }

    // Determine if it has decimals
    const hasDecimal = numberStr.includes('.');

    // Format the number with proper Polish separators
    if (hasDecimal) {
      // Get integer and decimal parts
      const parts = numericValue.toFixed(2).split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Add thousands separator (space) to integer part
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return `${formattedInteger},${decimalPart}`;
    } else {
      const formattedInteger = numericValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return formattedInteger;
    }
  }

  private formatNumberForCEDot(numberStr: string): string {
    // Convert US format (1234.56 or 1,234.56) to CE dot format (1.234,56)
    const numericValue = parseFloat(numberStr.replace(/,/g, ''));
    if (isNaN(numericValue)) {
      return numberStr;
    }
    const hasDecimal = numberStr.includes('.');
    if (hasDecimal) {
      const parts = numericValue.toFixed(2).split('.');
      const formattedInteger = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${formattedInteger},${parts[1]}`;
    } else {
      return numericValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  }
  private getCSVDelimiterForLocale(locale: string): string {
    try {
      // Get all available language preferences, not just the first one
      const languages = navigator.languages || [navigator.language] || ['en-US'];
      // Check all language preferences, not just the primary one
      const isEuropeanLanguage = languages.some(lang => {
        const [languageCode] = lang.toLowerCase().split('-');
        return europeanLocales.has(languageCode);
      });
      // Also check system locale through Intl API (more reliable on Windows)
      const systemLocale = Intl.NumberFormat().resolvedOptions().locale;
      const [systemLanguageCode] = systemLocale.toLowerCase().split('-');
      const isSystemEuropean = europeanLocales.has(systemLanguageCode);
      return isEuropeanLanguage || isSystemEuropean ? ';' : ',';
    } catch (error) {
      this.logger.warn(`Error determining CSV delimiter for locale: ${error}`);
      // Fallback to original logic if Intl API fails
      const [languageCode] = (locale || 'en-US').toLowerCase().split('-');
      const isEuropeanLanguage = europeanLocales.has(languageCode);
      return isEuropeanLanguage ? ';' : ',';
    }
  }
  public isNumericString(input: string): boolean {
    // Match patterns like: 123-456, 1,6300, 1,234,567, etc.
    const numericStringRegex = /^[0-9]+([,-][0-9]+)+$/;
    return numericStringRegex.test(input);
  }
  public generateTableViewCSVFile(
    dataToExport: string[][],
    entity: EntityType,
    featureInUse?: FeatureInUseExcel,
    requestId?: string,
    fileName?: string,
    fileExtension: string = 'csv'
  ): void {
    fileName =
      fileName ||
      `MIQ_` +
        `${featureInUse}_${entity}_${moment().format('MMDDYYYY')}_${
          requestId?.split('-')[0]
        }.${fileExtension}`.toLowerCase();
    this.buildAndDownloadWithSheetJs(dataToExport, entity, fileName);
  }
  public appendUOMColumns(flatColumns: Column[], selectedColumns?: Column[]): Column[] {
    const processedColumns = [];
    const columnsToProcess = selectedColumns?.length > 0 ? selectedColumns : flatColumns;
    for (const eachValue of columnsToProcess) {
      processedColumns.push(eachValue);
      const uomColumn = flatColumns.filter(value => {
        const columnConfig = this.columnExtensions[value.columnId];
        return eachValue.columnId === value.columnId && columnConfig?.uomId !== -1 && value.unit;
      });
      if (uomColumn.length > 0) {
        processedColumns.push(uomColumn[0].unit); // appends UOM column when unit is populated for selected column
      }
    }
    return processedColumns;
  }
}
